import { useState, useCallback, useRef, useEffect } from 'react';
import { geminiService } from '../services/geminiService';
import { ConversationMessage } from '../types/gemini';
import { Message } from '../types/chat';

interface UseGeminiStreamResult {
  isStreaming: boolean;
  isLoading: boolean;
  error: string | null;
  sendMessage: (message: string, systemPrompt: string, conversationHistory: Message[]) => Promise<void>;
  abortStream: () => void;
}

export const useGeminiStream = (
  onStreamChunk: (chunk: string, messageId: string) => void,
  onStreamComplete: (messageId: string, fullMessage: string, metadata?: any) => void,
  onError: (error: string) => void
): UseGeminiStreamResult => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Stable refs for callbacks — avoids recreating sendMessage on every render
  const onStreamChunkRef = useRef(onStreamChunk);
  const onStreamCompleteRef = useRef(onStreamComplete);
  const onErrorRef = useRef(onError);
  onStreamChunkRef.current = onStreamChunk;
  onStreamCompleteRef.current = onStreamComplete;
  onErrorRef.current = onError;

  // Use ref for isStreaming guard so sendMessage callback stays stable
  const isStreamingRef = useRef(false);

  // Abort any in-flight stream on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  const sendMessage = useCallback(async (
    message: string,
    systemPrompt: string,
    conversationHistory: Message[]
  ) => {
    if (isStreamingRef.current) {
      console.warn('Already streaming, ignoring new message');
      return;
    }

    isStreamingRef.current = true;
    setIsLoading(true);
    setIsStreaming(true);
    setError(null);

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    const messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const startTime = performance.now();
    let fullMessage = '';
    let tokenCount = 0;

    try {
      // Validate message first
      const isValid = await geminiService.validateMessage(message);

      if (!isValid) {
        setIsLoading(false);

        // Generate friendly off-topic response
        const offTopicResponse = geminiService.generateOffTopicResponse();

        // Simulate streaming for consistency — send word-by-word deltas
        const words = offTopicResponse.split(' ');

        for (let i = 0; i < words.length; i++) {
          if (abortControllerRef.current?.signal.aborted) {
            return;
          }

          const wordChunk = (i === 0 ? '' : ' ') + words[i];
          onStreamChunkRef.current(wordChunk, messageId);

          // Small delay to simulate natural streaming
          await new Promise(resolve => setTimeout(resolve, 80));
        }

        // Complete the off-topic response
        onStreamCompleteRef.current(messageId, offTopicResponse, {
          tokensUsed: await geminiService.estimateTokens(offTopicResponse),
          responseTime: Math.round(performance.now() - startTime),
          confidence: 1.0,
          isOffTopicRedirect: true
        });

        return;
      }

      // Convert conversation history to Gemini format
      const geminiMessages: ConversationMessage[] = conversationHistory
        .slice(-8) // Keep only last 8 messages for context
        .map(msg => ({
          role: msg.role === 'assistant' ? 'model' : 'user',
          parts: [{ text: msg.content }]
        }));

      // Add current message
      geminiMessages.push({
        role: 'user',
        parts: [{ text: message }]
      });

      setIsLoading(false);

      // Start streaming
      const streamGenerator = await geminiService.generateStreamingResponse(
        geminiMessages,
        systemPrompt
      );

      for await (const chunk of streamGenerator) {
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        if (chunk.text) {
          fullMessage += chunk.text;
          tokenCount += await geminiService.estimateTokens(chunk.text);
          onStreamChunkRef.current(chunk.text, messageId);
        }

        if (chunk.finishReason === 'STOP' || chunk.finishReason === 'ERROR') {
          break;
        }
      }

      const responseTime = performance.now() - startTime;

      // Stream complete
      onStreamCompleteRef.current(messageId, fullMessage, {
        tokensUsed: tokenCount,
        responseTime: Math.round(responseTime),
        confidence: 0.95
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('Streaming error:', err);
      setError(errorMessage);
      onErrorRef.current(errorMessage);
    } finally {
      isStreamingRef.current = false;
      setIsStreaming(false);
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, []); // Stable — no deps needed, all external values accessed via refs

  const abortStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      isStreamingRef.current = false;
      setIsStreaming(false);
      setIsLoading(false);
      setError(null);
    }
  }, []);

  return {
    isStreaming,
    isLoading,
    error,
    sendMessage,
    abortStream
  };
};
